import { createFileRoute } from "@tanstack/react-router";
import { SEOPage } from "@/components/site/SEOPage";
import { MessageSquare, RefreshCcw, Heart, ShieldCheck, Zap, TrendingUp, Users, Smartphone } from "lucide-react";

const ArticlePage = () => {
  const breadcrumbs = [
    { label: "Elite", href: "/" },
    { label: "Blog", href: "/blog" },
    { label: "Guia de Pós-Venda IPTV", href: "/blog/guia-pos-venda-iptv" }
  ];

  const sections = [
    {
      title: "1. O Pós-Venda Começa no Momento do Pagamento",
      content: (
        <div className="prose prose-invert max-w-none">
          <p className="text-lg leading-relaxed text-muted-foreground mb-6">
            Muitos revendedores cometem o erro fatal de achar que o trabalho termina quando o dinheiro cai na conta. Na verdade, para um negócio de assinatura (recorrência) como o IPTV, o trabalho de verdade começa agora. O pós-venda é o conjunto de ações que garantem que o cliente não apenas use o serviço, mas que esteja satisfeito o suficiente para pagar o próximo mês.
          </p>
          <p className="text-lg leading-relaxed text-muted-foreground mb-6">
            O primeiro contato pós-venda deve ser a entrega imediata dos dados de acesso acompanhada de um tutorial simples e visual. Se o cliente demora a conseguir logar, a primeira experiência dele é de frustração. Sua missão é garantir o "sucesso do cliente" nos primeiros 15 minutos.
          </p>
        </div>
      )
    },
    {
      title: "2. Estratégias de Retenção Ativa",
      type: "featured" as const,
      content: (
        <div className="space-y-8">
          <div className="grid md:grid-cols-2 gap-8">
            <div className="bg-black/30 p-8 rounded-3xl border border-white/5 space-y-4">
              <h4 className="text-white text-xl font-bold mb-4 flex items-center gap-2"><MessageSquare className="w-6 h-6 text-electric" /> Check-in de 3 Dias</h4>
              <p className="text-sm text-muted-foreground">Após 72 horas da venda, envie uma mensagem curta: "Olá, tudo bem? Passando para saber se deu tudo certo com a instalação e se está gostando do conteúdo". Isso mostra cuidado e resolve problemas antes que virem cancelamentos.</p>
            </div>
            <div className="bg-black/30 p-8 rounded-3xl border border-white/5 space-y-4">
              <h4 className="text-white text-xl font-bold mb-4 flex items-center gap-2"><RefreshCcw className="w-6 h-6 text-amber-500" /> Renovação Antecipada</h4>
              <p className="text-sm text-muted-foreground">Entre em contato 3 a 5 dias antes do vencimento. Ofereça um bônus ou apenas lembre do prazo. Clientes esquecem, e se o sinal cair antes da cobrança, a chance de ele procurar outro fornecedor é maior.</p>
            </div>
          </div>
        </div>
      )
    },
    {
      title: "3. Transformando Problemas em Lealdade",
      content: (
        <div className="prose prose-invert max-w-none space-y-8">
          <p className="text-muted-foreground">Quando um cliente te procura com um problema técnico, essa é a sua maior oportunidade de fidelização. Um suporte rápido e resolutivo gera um nível de confiança impossível de conseguir apenas com o serviço funcionando perfeitamente.</p>
          <div className="flex gap-6 items-start">
            <div className="w-12 h-12 rounded-2xl bg-electric/20 text-electric flex items-center justify-center flex-shrink-0 font-black italic">1</div>
            <div>
              <h4 className="text-white text-xl font-bold mb-2">Assuma a Responsabilidade</h4>
              <p className="text-muted-foreground">Mesmo que o problema seja a internet do cliente, ajude-o a diagnosticar. Sugira trocar o DNS, usar cabo de rede ou reiniciar o roteador. Quando você se torna o consultor técnico dele, ele não te troca por ninguém.</p>
            </div>
          </div>
          <div className="flex gap-6 items-start">
            <div className="w-12 h-12 rounded-2xl bg-electric/20 text-electric flex items-center justify-center flex-shrink-0 font-black italic">2</div>
            <div>
              <h4 className="text-white text-xl font-bold mb-2">Comunicação de Manutenção</h4>
              <p className="text-muted-foreground">Nunca deixe o cliente descobrir sozinho que um canal está em manutenção. Use o Status do WhatsApp ou listas de transmissão para informar: "Estamos atualizando a grade de esportes para garantir 4K na rodada de hoje".</p>
            </div>
          </div>
        </div>
      )
    },
    {
      title: "4. Métricas de Pós-Venda",
      content: (
        <div className="prose prose-invert max-w-none">
          <p className="text-muted-foreground mb-6">Para saber se o seu pós-venda está funcionando, acompanhe estes dois indicadores:</p>
          <ul className="space-y-4">
            <li className="flex items-start gap-4">
              <TrendingUp className="w-6 h-6 text-electric mt-1 flex-shrink-0" />
              <div>
                <h5 className="font-bold text-white">Taxa de Renovação (Churn)</h5>
                <p className="text-sm text-muted-foreground">Quantos clientes pagaram o segundo mês? Se for menos de 70%, seu pós-venda ou a qualidade do seu servidor precisam de ajustes imediatos.</p>
              </div>
            </li>
            <li className="flex items-start gap-4">
              <Users className="w-6 h-6 text-electric mt-1 flex-shrink-0" />
              <div>
                <h5 className="font-bold text-white">Indicações por Cliente</h5>
                <p className="text-sm text-muted-foreground">Um cliente feliz no pós-venda deve te trazer pelo menos uma indicação a cada 3 meses. Incentive isso com programas de indicação premiada.</p>
              </div>
            </li>
          </ul>
        </div>
      )
    }
  ];

  return (
    <div className="relative">
      <SEOPage
        category="Guias IPTV"
        title="Guia de Pós-Venda IPTV: O Segredo da Recorrência Infinita"
        subtitle="Vender é fácil, manter é o desafio. Aprenda como estruturar um pós-venda de elite que garante que seus clientes nunca queiram sair da sua revenda."
        heroImage="https://images.unsplash.com/photo-1552581234-26160f608093?auto=format&fit=crop&q=80&w=1200"
        introParagraphs={[
          "No mercado de IPTV, o lucro real não está na primeira venda, mas sim na segunda, na terceira e na décima renovação do mesmo cliente. Captar um cliente novo custa até 7 vezes mais caro do que manter um antigo. Por isso, o pós-venda não é um 'extra', é o coração financeiro do seu negócio.",
          "Neste guia completo, vamos detalhar o processo de acompanhamento que os revendedores que faturam acima de 5 dígitos mensais utilizam. Vamos falar sobre automação de lembretes, como lidar com problemas técnicos sem perder o cliente e como criar uma comunidade de usuários fiéis que defendem sua marca.",
          "Prepare-se para reduzir seus cancelamentos (churn) drasticamente e ver seu faturamento crescer de forma acumulada mês após mês. O segredo da escala está na base."
        ]}
        breadcrumbs={breadcrumbs}
        faq={[
          {
            question: "O que fazer quando o cliente decide cancelar?",
            answer: "Tente entender o motivo. Se for técnico, ofereça uma solução ou um novo teste em outro app. Se for financeiro, ofereça um plano mais acessível ou um desconto pontual. Se ele sair, deixe as portas abertas com educação."
          },
          {
            question: "Como organizar centenas de datas de vencimento?",
            answer: "Utilize as ferramentas de gestão do seu painel P2 PRO ou integre com um CRM simples. Nunca confie apenas na memória ou em anotações de papel."
          }
        ]}
        benefits={[
          {
            title: "Faturamento Previsível",
            description: "Saiba exatamente quanto vai entrar no próximo mês baseado na sua taxa de retenção.",
            icon: <Zap className="w-6 h-6" />
          },
          {
            title: "Redução de Esforço",
            description: "É muito mais fácil renovar um cliente satisfeito do que convencer um desconhecido a testar.",
            icon: <Heart className="w-6 h-6" />
          }
        ]}
        howItWorks={[
          {
            title: "Entrega de Valor",
            description: "Garanta que o cliente saiba usar todas as funcionalidades do app e tenha a melhor experiência inicial."
          },
          {
            title: "Acompanhamento Constante",
            description: "Mantenha o contato vivo através de novidades no conteúdo e suporte proativo antes de problemas surgirem."
          }
        ]}
        diferenciais={[
          {
            title: "Suporte P2 PRO de Elite",
            description: "Nossa tecnologia facilita seu pós-venda ao oferecer estabilidade e apps intuitivos que não dão trabalho.",
            icon: <ShieldCheck className="w-6 h-6" />
          }
        ]}
        sections={sections}
        ctaTitle="📈 Quer Ver seu Faturamento Crescer Todo Mês?"
        ctaDescription="Foque no seu pós-venda e deixe a estabilidade tecnológica com a P2 PRO. Juntos, vamos construir uma carteira de clientes indestrutível."
        ctaButtonText="Comece sua Revenda de Sucesso"
      />
    </div>
  );
};

export const Route = createFileRoute("/blog/guia-pos-venda-iptv")({
  head: () => ({
    meta: [
      { title: "Guia de Pós-Venda IPTV: O Segredo da Recorrência Infinita" },
      { name: "description", content: "Domine o pós-venda na revenda IPTV. Aprenda estratégias de retenção, automação de renovações e como reduzir o cancelamento de clientes para escalar seu lucro." },
    ],
  }),
  component: ArticlePage,
});